import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import * as authApi from "../api/auth";
import { TOKEN_STORAGE_KEY } from "../api/client";
import { User } from "../types";

interface AuthContextValue {
  user: User | null;
  /**
   * Permessi effettivi dell'utente (default del ruolo + override per-utente),
   * forniti dal backend su login/me. Fonte per menu e route guard: sostituiscono
   * le liste di ruoli hardcodate che divergevano da backend e route.
   */
  permissions: string[];
  /** true durante il bootstrap iniziale (verifica token salvato) */
  isLoading: boolean;
  isAuthenticated: boolean;
  /** true se l'utente ha il permesso nominato indicato. */
  hasPermission: (permission: string) => boolean;
  login: (identifier: string, password: string) => Promise<User>;
  logout: () => void;
  /** Aggiorna l'utente in memoria dopo una PATCH /auth/me o un cambio password */
  setUser: (user: User) => void;
  /** Imposta i permessi effettivi (es. subito dopo la registrazione via invito). */
  setPermissions: (permissions: string[]) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<User | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    setUserState(null);
    setPermissions([]);
  }, []);

  // Bootstrap: se c'è un token salvato, verifica che sia ancora valido
  // caricando il profilo corrente. Se il token è scaduto/non valido,
  // l'interceptor axios (api/client.ts) scatena "auth:unauthorized" e
  // l'effetto sotto fa il logout automaticamente.
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);

    if (!token) {
      setIsLoading(false);
      return;
    }

    authApi
      .me()
      .then(({ user: currentUser, permissions: perms }) => {
        setUserState(currentUser);
        setPermissions(perms);
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        setUserState(null);
        setPermissions([]);
      })
      .finally(() => setIsLoading(false));
  }, []);

  // Logout automatico su 401 da qualunque chiamata API (token scaduto/revocato).
  useEffect(() => {
    const handleUnauthorized = () => {
      setUserState(null);
      setPermissions([]);
    };
    window.addEventListener("auth:unauthorized", handleUnauthorized);
    return () => window.removeEventListener("auth:unauthorized", handleUnauthorized);
  }, []);

  // Il backend ha risposto 403 MUST_CHANGE_PASSWORD: lo stato in memoria dice
  // il contrario, ed è lui a essere indietro (sessione aperta prima del reset,
  // profilo caricato prima che il flag cambiasse). Basta accendere il flag:
  // ProtectedRoute porta a /cambio-password al render successivo.
  useEffect(() => {
    const handleMustChangePassword = () => {
      setUserState((prev) => (prev && !prev.must_change_password ? { ...prev, must_change_password: true } : prev));
    };
    window.addEventListener("auth:must-change-password", handleMustChangePassword);
    return () => window.removeEventListener("auth:must-change-password", handleMustChangePassword);
  }, []);

  const login = useCallback(async (identifier: string, password: string) => {
    const { token, user: loggedInUser, permissions: perms } = await authApi.login(identifier, password);
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
    setUserState(loggedInUser);
    setPermissions(perms);
    return loggedInUser;
  }, []);

  const hasPermission = useCallback(
    (permission: string) => permissions.includes(permission),
    [permissions]
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      permissions,
      isLoading,
      isAuthenticated: user !== null,
      hasPermission,
      login,
      logout,
      setUser: setUserState,
      setPermissions,
    }),
    [user, permissions, isLoading, hasPermission, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth deve essere usato all'interno di <AuthProvider>.");
  }
  return ctx;
}
